import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getAnalyticsLeadsByStatus, getAnalyticsLeadsByProject, getAnalyticsLeadsDynamics, getAnalyticsLeadsByManager, getAnalyticsTotalLeads, getUsers, getProjects, getAnalyticsLeadsByClientStatus, getAnalyticsLnbStats, getAnalyticsHotStats, getAnalyticsApprovedSentLeads } from '../api';
import Autocomplete from '@mui/material/Autocomplete';

export const AnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [leadsByStatus, setLeadsByStatus] = React.useState<any[]>([]);
  const [leadsByProject, setLeadsByProject] = React.useState<any[]>([]);
  const [leadsByManager, setLeadsByManager] = React.useState<any[]>([]);
  const [leadsDynamics, setLeadsDynamics] = React.useState<any[]>([]);
  const [totalLeads, setTotalLeads] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dateFrom, setDateFrom] = React.useState<string>('');
  const [dateTo, setDateTo] = React.useState<string>('');
  const [managerId, setManagerId] = React.useState<string>('');
  const [projectId, setProjectId] = React.useState<string>('');
  const [managers, setManagers] = React.useState<any[]>([]);
  const [projects, setProjects] = React.useState<any[]>([]);
  const [leadsByClientStatus, setLeadsByClientStatus] = React.useState<any[]>([]);
  const [lnbStats, setLnbStats] = React.useState<{
    sent_from_lnb: number;
    approved_from_lnb: number;
    conversion_rate: number;
  } | null>(null);
  const [hotStats, setHotStats] = React.useState<{
    sent_from_hot: number;
    approved_from_hot: number;
    conversion_rate: number;
  } | null>(null);
  const [approvedSentLeads, setApprovedSentLeads] = React.useState<{
    data: Array<{ manager_name: string; project_name: string; count: number }>;
    totals: {
      by_manager: Record<string, number>;
      by_project: Record<string, number>;
      total: number;
    };
  } | null>(null);

  const fetchAnalytics = React.useCallback(async () => {
    setLoading(true);
    try {
      const from = dateFrom || undefined;
      const to = dateTo || undefined;
      const manager = managerId || undefined;
      const project = projectId || undefined;
      const [statusData, projectData, dynamicsData, managerData, totalData, clientStatusData, lnbStatsData, hotStatsData, approvedSentData] = await Promise.all([
        getAnalyticsLeadsByStatus(from, to, manager, project),
        getAnalyticsLeadsByProject(from, to, manager, project),
        getAnalyticsLeadsDynamics(30, from, to, manager, project),
        getAnalyticsLeadsByManager(from, to, manager, project),
        getAnalyticsTotalLeads(from, to, manager, project),
        getAnalyticsLeadsByClientStatus(from, to, manager, project),
        getAnalyticsLnbStats(from, to, manager, project),
        getAnalyticsHotStats(from, to, manager, project),
        getAnalyticsApprovedSentLeads(from, to),
      ]);
      setLeadsByStatus(statusData);
      setLeadsByProject(projectData);
      setLeadsDynamics(dynamicsData);
      setLeadsByManager(managerData);
      setTotalLeads(totalData?.total || 0);
      setLeadsByClientStatus(clientStatusData);
      setLnbStats(lnbStatsData);
      setHotStats(hotStatsData);
      setApprovedSentLeads(approvedSentData);
      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }, [dateFrom, dateTo, managerId, projectId]);

  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [usersData, projectsData] = await Promise.all([
          getUsers(),
          getProjects()
        ]);
        // Сортируем менеджеров по имени
        const sortedManagers = [...usersData].sort((a, b) => {
          const nameA = (a.operator_name || a.email || '').toLowerCase();
          const nameB = (b.operator_name || b.email || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        // Сортируем проекты по имени
        const sortedProjects = [...projectsData].sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setManagers(sortedManagers);
        setProjects(sortedProjects);
      } catch (err: any) {
        console.error('Failed to fetch managers/projects:', err);
      }
    };
    fetchInitialData();
  }, []);

  React.useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) return <Typography>{t('loading') || 'Loading...'}</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
      <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>{t('analytics') || 'Analytics'}</Typography>
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: isMobile ? 'stretch' : 'center', mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: isMobile ? '1' : 'none' }}>
            <TextField
              label={t('date_from') || 'Дата с'}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 130 }}
            />
            <TextField
              label={t('date_to') || 'Дата по'}
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 130 }}
            />
          </Box>
          <TextField
            select
            label="Менеджер"
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            size="small"
            sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? '1' : 'none' }}
          >
            <MenuItem value="">Все менеджеры</MenuItem>
            {managers.map((manager) => (
              <MenuItem key={manager.id} value={manager.id}>
                {manager.operator_name || manager.email}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={projects}
            getOptionLabel={option => option.name}
            value={projects.find(p => String(p.id) === String(projectId)) || null}
            onChange={(event, newValue) => setProjectId(newValue ? String(newValue.id) : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label="Проект" placeholder="Найти проект..." size="small" />
            )}
            sx={{ minWidth: isMobile ? '100%' : 200, flex: isMobile ? '1' : 'none' }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={fetchAnalytics} fullWidth={isMobile} sx={{ flex: 1 }}>{t('apply') || 'Применить'}</Button>
            <Button variant="text" onClick={() => { setDateFrom(''); setDateTo(''); setManagerId(''); setProjectId(''); }} sx={{ flex: isMobile ? 1 : 'none' }}>{t('reset') || 'Сбросить'}</Button>
          </Stack>
        </Box>
      </Box>
      
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('total_leads') || 'Всего лидов'}</Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography>{t('selected_period') || 'За период'}</Typography>
            <Typography variant="h4">{totalLeads}</Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('leads_by_status') || 'Leads by Status'}</Typography>
          {leadsByStatus.map((item: any) => (
            <Box key={item.status} display="flex" justifyContent="space-between" mb={1}>
              <Typography>{item.status}</Typography>
              <Typography variant="h6">{item.count}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('leads_by_project') || 'Leads by Project'}</Typography>
          {leadsByProject.map((item: any, idx: number) => (
            <Box key={`${item.project_name}-${idx}`} display="flex" justifyContent="space-between" mb={1}>
              <Typography>{item.project_name}</Typography>
              <Typography variant="h6">{item.count}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('sent_by_managers') || 'Отправлено менеджерами'}</Typography>
          {leadsByManager.map((item: any, idx: number) => (
            <Box key={`${item.manager_name}-${idx}`} display="flex" justifyContent="space-between" mb={1}>
              <Typography>{item.manager_name}</Typography>
              <Typography variant="h6">{item.count}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{t('leads_dynamics') || 'Leads Dynamics (30 days)'}</Typography>
            {leadsDynamics.map((item: any) => (
              <Box key={item.date} display="flex" justifyContent="space-between" mb={1}>
                <Typography>{item.date}</Typography>
                <Typography variant="h6">{item.count}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Leads by Client Status</Typography>
          {leadsByClientStatus.map((item: any, idx: number) => (
            <Box key={`${item.client_status ?? 'NONE'}-${idx}`} display="flex" justifyContent="space-between" mb={1}>
              <Typography>{item.client_status ?? 'NONE'}</Typography>
              <Typography variant="h6">{item.count}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Статистика по ЛНБ</Typography>
          {lnbStats && (
            <>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Отправлено из ЛНБ</Typography>
                <Typography variant="h6">{lnbStats.sent_from_lnb}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Утверждено (В работе + Завершено)</Typography>
                <Typography variant="h6">{lnbStats.approved_from_lnb}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Конверсия</Typography>
                <Typography variant="h6">{lnbStats.conversion_rate}%</Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Статистика по Горячим</Typography>
          {hotStats && (
            <>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Отправлено Горячих</Typography>
                <Typography variant="h6">{hotStats.sent_from_hot}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Утверждено (В работе + Завершено + Принято)</Typography>
                <Typography variant="h6">{hotStats.approved_from_hot}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Конверсия</Typography>
                <Typography variant="h6">{hotStats.conversion_rate}%</Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Утвержденные лиды по операторам</Typography>
            {approvedSentLeads && approvedSentLeads.data.length > 0 ? (
              <>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 400 }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                        <Box component="th" sx={{ textAlign: 'left', p: 1 }}>Оператор</Box>
                        <Box component="th" sx={{ textAlign: 'left', p: 1 }}>Проект</Box>
                        <Box component="th" sx={{ textAlign: 'right', p: 1 }}>Кол-во</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {approvedSentLeads.data.map((item, idx) => (
                        <Box component="tr" key={`${item.manager_name}-${item.project_name}-${idx}`} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box component="td" sx={{ p: 1 }}>{item.manager_name}</Box>
                          <Box component="td" sx={{ p: 1 }}>{item.project_name}</Box>
                          <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: 'bold' }}>{item.count}</Box>
                        </Box>
                      ))}
                    </Box>
                    <Box component="tfoot">
                      <Box component="tr" sx={{ borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                        <Box component="td" colSpan={2} sx={{ p: 1, fontWeight: 'bold' }}>Итого</Box>
                        <Box component="td" sx={{ p: 1, textAlign: 'right', fontWeight: 'bold' }}>{approvedSentLeads.totals.total}</Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>По операторам:</Typography>
                    {Object.entries(approvedSentLeads.totals.by_manager).map(([name, count]) => (
                      <Box key={name} display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{name}</Typography>
                        <Typography variant="body2" fontWeight="bold">{count}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="subtitle2" gutterBottom>По проектам:</Typography>
                    {Object.entries(approvedSentLeads.totals.by_project).map(([name, count]) => (
                      <Box key={name} display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2">{name}</Typography>
                        <Typography variant="body2" fontWeight="bold">{count}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary">Нет данных за выбранный период</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}; 